import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { asapScheduler, filter, observeOn, Subscription } from 'rxjs';

import { ContactComponent } from '@shared/components/contact/contact.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { ImpersonationBannerComponent } from '@shared/components/impersonation-banner/impersonation-banner.component';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { ButtonConfig } from '@shared/models/button.model';
import { ImpersonationState } from '@shared/models/impersonation.model';
import { AuthService } from '@core/auth/auth.service';
import { SessionTimeoutState } from '@core/auth/session-timeout/session-timeout.models';
import { SessionTimeoutService } from '@core/auth/session-timeout/session-timeout.service';
import { openSessionTimeoutDialog } from '@core/auth/session-timeout/session-timeout-dialog';
import { PORTAL_ANALYTICS_EVENT_NAMES } from '@core/insights/portal-analytics.models';
import { PortalAnalyticsService } from '@core/insights/portal-analytics.service';
import { DashboardService } from '@features/dashboard/services/dashboard.service';
import { DashboardHeaderComponent } from '@features/home/components/header/dashboard-header.component';
import { DashboardSidebarComponent } from '@features/home/components/sidebar/dashboard-sidebar.component';
import { UserService } from '@features/organization/services/user.service';

import { environment } from '../environments/environment';

import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  InteractionType,
  PopupRequest,
  PromptValue,
  RedirectRequest,
  SsoSilentRequest,
} from '@azure/msal-browser';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

const B2C_ERROR_FORGOT_PASSWORD = 'AADB2C90118';
const B2C_ERROR_USER_CANCELED = 'AADB2C90091';

@Component({
  selector: 'srm-root',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    ContactComponent,
    DashboardSidebarComponent,
    DashboardHeaderComponent,
    LoadingSpinnerComponent,
    ImpersonationBannerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('pageContent') private pageContent!: ElementRef<HTMLDivElement>;

  private subscriptions = new Subscription();
  private msalCallbackId: string | null = null;
  private dashboardService = inject(DashboardService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private portalAnalytics = inject(PortalAnalyticsService);
  private sessionTimeoutService = inject(SessionTimeoutService);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private sessionTimeoutDialogRef: MatDialogRef<DialogComponent, unknown> | null = null;
  private hasTrackedLoginForSession = false;

  public pageTitle: string = '';
  public pageSubtitle: string | null = null;
  public showMainLayout: boolean = false;
  public headerButtonConfig: ButtonConfig | null = null;
  public secondaryHeaderButtonConfig: ButtonConfig | null = null;
  public isFullscreenLayout: boolean = false;
  public fontsLoaded: boolean = false;
  public impersonationState: ImpersonationState | null = null;
  public isStoppingImpersonation: boolean = false;

  public constructor(@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration) {}

  public ngOnInit(): void {
    this.preloadFonts();
    this.setupRouterEvents();
    this.setupMsalRedirectHandling();
    this.setupMsalSuccessEvents();
    this.setupMsalFailureEvents();
    this.setupMsalEventCallback();
    this.setupDashboardSubscriptions();
    this.setupImpersonationSubscription();
    this.setupSessionTimeoutSubscription();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.msalCallbackId) {
      this.msalService.instance.removeEventCallback(this.msalCallbackId);
    }

    this.closeSessionTimeoutWarningDialog();
  }

  public login(userFlowRequest?: RedirectRequest | PopupRequest): void {
    const authRequest = this.buildAuthRequest(userFlowRequest);

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.subscriptions.add(
        this.msalService.loginPopup(authRequest).subscribe((response: AuthenticationResult) => {
          this.setActiveAccountAndUpdateLayout(response.account);
        }),
      );
    } else {
      this.msalService.loginRedirect(authRequest);
    }
  }

  public onHeaderButtonClicked(): void {
    this.dashboardService.notifyButtonClick();
  }

  public onSecondaryHeaderButtonClicked(): void {
    this.dashboardService.notifySecondaryButtonClick();
  }

  public onImpersonationStopClicked(): void {
    if (this.isStoppingImpersonation) {
      return;
    }

    this.isStoppingImpersonation = true;
    this.cdr.markForCheck();

    this.subscriptions.add(
      this.userService.stopImpersonation().subscribe({
        next: () => {
          void this.authService.logout();
        },
        error: (error: unknown) => {
          console.error('Failed to stop impersonation before logout', error);
          this.isStoppingImpersonation = false;
          this.cdr.markForCheck();
        },
      }),
    );
  }

  private setupRouterEvents(): void {
    const initialSnapshot = this.router.routerState.snapshot.root.children[0];
    this.isFullscreenLayout = initialSnapshot?.data['layout'] === 'fullscreen';
    this.updateMainLayoutVisibility();

    this.subscriptions.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        if (this.pageContent) {
          this.pageContent.nativeElement.scrollTop = 0;
        }
        const snapshot = this.router.routerState.snapshot.root.children[0];
        this.isFullscreenLayout = snapshot?.data['layout'] === 'fullscreen';
        this.updateMainLayoutVisibility();
      }),
    );
  }

  private setupMsalRedirectHandling(): void {
    this.subscriptions.add(
      this.msalService.handleRedirectObservable().subscribe({
        next: (result) => {
          if (result && result.account) {
            this.setActiveAccountAndUpdateLayout(result.account);
          }
        },
        error: (error: unknown) => {
          this.showMainLayout = false;
          this.cdr.markForCheck();

          if (this.hasB2CErrorCode(error, B2C_ERROR_USER_CANCELED)) {
            this.login();
          }
        },
      }),
    );
  }

  private setupMsalSuccessEvents(): void {
    this.subscriptions.add(
      this.msalBroadcastService.msalSubject$
        .pipe(
          filter(
            (msg: EventMessage) =>
              msg.eventType === EventType.LOGIN_SUCCESS ||
              msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
              msg.eventType === EventType.SSO_SILENT_SUCCESS,
          ),
        )
        .subscribe((result: EventMessage) => {
          const authResult = this.asAuthenticationResult(result.payload);
          if (!authResult) {
            return result;
          }

          this.updateSessionExpiryFromAuthResult(authResult);
          this.handleMsalSuccess(
            authResult,
            result.eventType === EventType.LOGIN_SUCCESS ||
              result.eventType === EventType.SSO_SILENT_SUCCESS,
          );

          return result;
        }),
    );
  }

  private setupMsalFailureEvents(): void {
    this.subscriptions.add(
      this.msalBroadcastService.msalSubject$
        .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE))
        .subscribe((result: EventMessage) => {
          this.portalAnalytics.trackPortalEvent(
            PORTAL_ANALYTICS_EVENT_NAMES.userLoginFailed,
            this.portalAnalytics.createEventPayload({
              event_category: 'behavior',
              feature: 'auth',
              source_surface: 'login',
              success: false,
              ...(this.portalAnalytics.getSafeErrorCode(result.error)
                ? { error_code: this.portalAnalytics.getSafeErrorCode(result.error) }
                : {}),
            }),
          );
          if (this.hasB2CErrorCode(result.error, B2C_ERROR_FORGOT_PASSWORD)) {
            // User clicked "Forgot Password" -> Redirect to Reset Flow
            const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
              authority: environment.b2cPolicies.authorities.resetPassword.authority,
              scopes: [],
            };
            this.login(resetPasswordFlowRequest);
          } else if (this.hasB2CErrorCode(result.error, B2C_ERROR_USER_CANCELED)) {
            this.login();
          }
        }),
    );
  }

  private setupMsalEventCallback(): void {
    this.msalCallbackId = this.msalService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = this.asAuthenticationResult(event.payload);
        if (!payload) {
          return;
        }

        this.setActiveAccountAndUpdateLayout(payload.account);
      }
    });
  }

  private setupDashboardSubscriptions(): void {
    this.subscriptions.add(
      this.dashboardService.pageTitle$.pipe(observeOn(asapScheduler)).subscribe((title) => {
        this.pageTitle = title;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.dashboardService.pageSubtitle$.pipe(observeOn(asapScheduler)).subscribe((subtitle) => {
        this.pageSubtitle = subtitle;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.add(
      this.dashboardService.headerButtonConfig$
        .pipe(observeOn(asapScheduler))
        .subscribe((config) => {
          this.headerButtonConfig = config;
          this.cdr.markForCheck();
        }),
    );

    this.subscriptions.add(
      this.dashboardService.secondaryHeaderButtonConfig$
        .pipe(observeOn(asapScheduler))
        .subscribe((config) => {
          this.secondaryHeaderButtonConfig = config;
          this.cdr.markForCheck();
        }),
    );
  }

  private setupImpersonationSubscription(): void {
    this.subscriptions.add(
      this.authService.impersonationState$.subscribe((state) => {
        this.impersonationState = state;
        this.cdr.markForCheck();
      }),
    );
  }

  private setupSessionTimeoutSubscription(): void {
    this.subscriptions.add(
      this.sessionTimeoutService.state$.subscribe((state) => {
        this.handleSessionTimeoutState(state);
      }),
    );
  }

  private preloadFonts(): void {
    void Promise.all([
      document.fonts.load('400 1rem "Inter"'),
      document.fonts.load('400 2rem "Financier Display"'),
    ])
      .then(() => {
        this.fontsLoaded = true;
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.fontsLoaded = true;
        this.cdr.markForCheck();
      });
  }

  private buildAuthRequest(
    userFlowRequest?: RedirectRequest | PopupRequest,
  ): RedirectRequest | PopupRequest | undefined {
    if (!this.msalGuardConfig.authRequest) {
      return userFlowRequest;
    }

    return {
      ...this.msalGuardConfig.authRequest,
      ...userFlowRequest,
    } as RedirectRequest | PopupRequest;
  }

  private setActiveAccountAndUpdateLayout(account: AccountInfo | null): void {
    this.msalService.instance.setActiveAccount(account);
    this.updateMainLayoutVisibility();
  }

  private updateMainLayoutVisibility(): void {
    this.showMainLayout = this.authService.isUserCurrentlyLoggedIn() && !this.isFullscreenLayout;
    this.cdr.markForCheck();
  }

  private hasB2CErrorCode(error: unknown, code: string): boolean {
    if (!error || typeof error !== 'object' || !('message' in error)) {
      return false;
    }

    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' && message.includes(code);
  }

  private handleMsalSuccess(
    authenticationResult: AuthenticationResult,
    shouldTrackLogin: boolean,
  ): void {
    const idtoken = this.asIdTokenClaimsWithPolicyId(authenticationResult.idTokenClaims);
    if (!idtoken) {
      this.updateMainLayoutVisibility();
      return;
    }

    if (this.isPolicyMatch(idtoken, environment.b2cPolicies.names.signUpSignIn)) {
      this.setActiveAccountAndUpdateLayout(authenticationResult.account);
      if (shouldTrackLogin && !this.hasTrackedLoginForSession) {
        this.hasTrackedLoginForSession = true;
        this.portalAnalytics.trackPortalEvent(
          PORTAL_ANALYTICS_EVENT_NAMES.userLogin,
          this.portalAnalytics.createEventPayload({
            event_category: 'adoption',
            feature: 'auth',
            source_surface: 'sign_up_sign_in_callback',
            success: true,
          }),
        );
      }
    }

    if (this.isPolicyMatch(idtoken, environment.b2cPolicies.names.editProfile)) {
      this.handleEditProfileSuccess(idtoken);
    }

    if (this.isPolicyMatch(idtoken, environment.b2cPolicies.names.resetPassword)) {
      this.handleResetPasswordSuccess(idtoken);
    }

    this.updateMainLayoutVisibility();
  }

  private isPolicyMatch(idtoken: IdTokenClaimsWithPolicyId | null, policyName: string): boolean {
    if (!idtoken) {
      return false;
    }

    return idtoken.acr === policyName || idtoken.tfp === policyName;
  }

  private handleEditProfileSuccess(idtoken: IdTokenClaimsWithPolicyId): void {
    const originalSignInAccount = this.msalService.instance
      .getAllAccounts()
      .find((account: AccountInfo) => {
        const accountClaims = account.idTokenClaims as IdTokenClaimsWithPolicyId | undefined;

        return (
          account.idTokenClaims?.oid === idtoken.oid &&
          account.idTokenClaims?.sub === idtoken.sub &&
          !!accountClaims &&
          this.isPolicyMatch(accountClaims, environment.b2cPolicies.names.signUpSignIn)
        );
      });

    const signUpSignInFlowRequest: SsoSilentRequest = {
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      account: originalSignInAccount,
    };

    this.msalService.ssoSilent(signUpSignInFlowRequest);
  }

  private handleResetPasswordSuccess(idtoken: IdTokenClaimsWithPolicyId): void {
    this.portalAnalytics.trackPortalEvent(
      PORTAL_ANALYTICS_EVENT_NAMES.userPasswordReset,
      this.portalAnalytics.createEventPayload({
        event_category: 'adoption',
        feature: 'auth',
        source_surface: 'reset_password_callback',
        success: true,
      }),
    );

    // The password of the signed-in account just changed, so the existing session is stale.
    // End it rather than silently reissuing tokens against credentials that no longer exist.
    if (this.isActiveAccount(idtoken)) {
      void this.authService.logout();
      return;
    }

    const signUpSignInFlowRequest: RedirectRequest | PopupRequest = {
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      scopes: [...environment.apiConfig.scopes],
      prompt: PromptValue.LOGIN,
    };

    this.login(signUpSignInFlowRequest);
  }

  private isActiveAccount(idtoken: IdTokenClaimsWithPolicyId): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      return false;
    }

    return (
      activeAccount.idTokenClaims?.oid === idtoken.oid &&
      activeAccount.idTokenClaims?.sub === idtoken.sub
    );
  }

  private handleSessionTimeoutState(state: SessionTimeoutState): void {
    if (state.status === 'active') {
      this.closeSessionTimeoutWarningDialog();
      return;
    }

    if (state.status === 'redirecting') {
      this.closeSessionTimeoutWarningDialog();
      return;
    }

    if (state.status === 'warning') {
      this.openSessionTimeoutWarningDialog();
      return;
    }

    if (state.status === 'expired') {
      this.closeSessionTimeoutWarningDialog();
      this.sessionTimeoutService.requestReauth(this.getCurrentUrl(), 'expired');
    }
  }

  private openSessionTimeoutWarningDialog(): void {
    if (this.sessionTimeoutDialogRef) {
      return;
    }

    const dialogHandle = openSessionTimeoutDialog(this.dialog);
    this.sessionTimeoutDialogRef = dialogHandle.dialogRef;
    this.subscriptions.add(
      dialogHandle.reloginRequested$.subscribe((reloginRequested) => {
        this.sessionTimeoutDialogRef = null;
        if (reloginRequested) {
          const reauthStarted = this.sessionTimeoutService.requestReauth(
            this.getCurrentUrl(),
            'warning',
          );

          if (!reauthStarted) {
            this.openSessionTimeoutWarningDialog();
          }
        }
      }),
    );
  }

  private closeSessionTimeoutWarningDialog(): void {
    if (!this.sessionTimeoutDialogRef) {
      return;
    }

    this.sessionTimeoutDialogRef.close(false);
    this.sessionTimeoutDialogRef = null;
  }

  private updateSessionExpiryFromAuthResult(authenticationResult: AuthenticationResult): void {
    this.sessionTimeoutService.updateExpiry(this.extractExpiresAt(authenticationResult));
  }

  private extractExpiresAt(authenticationResult: AuthenticationResult): Date | null {
    if (authenticationResult.expiresOn instanceof Date) {
      return authenticationResult.expiresOn;
    }

    const tokenClaims = authenticationResult.idTokenClaims as Record<string, unknown> | undefined;
    const expClaim = tokenClaims?.['exp'];
    if (typeof expClaim === 'number') {
      return new Date(expClaim * 1000);
    }

    if (typeof expClaim === 'string') {
      const expSeconds = Number(expClaim);
      if (!Number.isNaN(expSeconds)) {
        return new Date(expSeconds * 1000);
      }
    }

    return null;
  }

  private getCurrentUrl(): string {
    return window.location.pathname + window.location.search;
  }

  private asAuthenticationResult(payload: unknown): AuthenticationResult | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    return payload as AuthenticationResult;
  }

  private asIdTokenClaimsWithPolicyId(claims: unknown): IdTokenClaimsWithPolicyId | null {
    if (!claims || typeof claims !== 'object') {
      return null;
    }

    return claims;
  }
}
